<script setup lang="ts">
import { AppBarMenu } from '@/components/AppBarMenu';
import { HeaderComponent } from '@/components/HeaderComponent';
import environment from '@/helpers/environment';
import { errorToast } from '@/helpers/toastHelpers';
import { monitoringService } from '@/services';
import { filterSiteTree, useACLStore } from '@itx-data/ui-helpers/';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref, type Ref } from 'vue';
import { app } from './main';
import type { MonitoringHealth, MonitoringInfo } from './services/monitoring-service';
import type { MenuItem } from './types/menu-item';

import inditexBlack from '@/assets/i/inditex.svg';
import { siteTree } from '@/components/MainMenu/sitetree';
import { useEventListener } from '@vueuse/core';
import i18n, { t, setI18nLocale } from '@/locales/i18n';
import { useLocaleStore } from '@/store/locale-store';
import { useRoute } from 'vue-router';

const env: string = environment.VITE_APP_ENV;
const instrumentationKey = environment.VITE_APP_INSIGHTS;
const portalLogo = inditexBlack;

const isScrolledOutsideHeader = ref(false);
const menuCollapse = ref(false);
const portalInfo: Ref<MonitoringInfo> = ref({});
const portalHealth: Ref<MonitoringHealth> = ref({});

const aclStore = useACLStore();
const { user, userId } = storeToRefs(aclStore);

useEventListener(document, 'scroll', () => {
  const video = document.getElementById('headerMedia');
  const appBar = document.getElementsByClassName('dp-app-bar');

  if (video && appBar) {
    isScrolledOutsideHeader.value = window.scrollY + appBar[0]!.clientHeight >= video.scrollHeight;
  }
});

const appBarSiteTree = computed<MenuItem[]>(() =>
  filterSiteTree<MenuItem>(siteTree.value, aclStore.roles, docsRouteBase.value)
);

const appClass = computed(() => ({
  app: true,
  'app--black': isScrolledOutsideHeader.value
}));

const docsRouteBase = computed(() => import.meta.env.VITE_APP_MARKDOWN_DOCS_ROUTE_BASE || '');

const localeStore = useLocaleStore();
const { locale } = storeToRefs(localeStore);

const route = useRoute(); 

onBeforeMount(() => {
  if (env !== 'local') {
    getMonitoringInfo();
    getMonitoringHealth();
  }
  setI18nLocale(locale.value);
});

function getMonitoringHealth() {
  monitoringService
    .getMonitoringHealth()
    .then((data) => {
      portalHealth.value = data.data;
    })
    .catch(() => {
      errorToast('Error getting monitoring health info');
    });
}

const getMonitoringInfo = () => {
  monitoringService
    .getMonitoringInfo()
    .then((data) => {
      portalInfo.value = data.data;
    })
    .catch(() => {
      errorToast('Error getting monitoring info');
    });
};
const getLocaleClass = (locale: string) => {
  return {
    app__locale: true,
    'app__locale--active': i18n.global.locale.value === locale
  };
};

const switchLocale = (locale: string) => {
  localeStore.setLocale(locale);
  if (route.meta.titleI18N) {
    document.title = t(route.meta.titleI18N as string);
  }
};

const shouldShowLocaleSelector = computed(() => 
  route.name === 'home' || route.name === 'manufacturing-process'
);
</script>

<template>
  <div :class="appClass">
    <DpApp
      v-model="menuCollapse"
      actions
      fixed
      ref="appBar"
      no-right-menu
      :logo="isScrolledOutsideHeader"
      :title="t('home.title')"
      :hide-apps-menu="true"
    >
      <template #logo>
        <img
          v-if="isScrolledOutsideHeader"
          alt="Inditex"
          class="dp-app__logo-black"
          :src="portalLogo"
        />
      </template>

      <RouterView v-slot="{ Component, route }">
        <HeaderComponent
          :manufacturing="route.params?.manufacturing as string"
          :type="route.params?.type as string"
          :measure="route.params?.measure as string"
        ></HeaderComponent>

        <Transition name="slide-fade">
          <div :key="route.fullPath">
            <component :is="Component" />
          </div>
        </Transition>
      </RouterView>

      <template #actions>
        <div v-if="shouldShowLocaleSelector" class="app__locale-container">
          <div class="app__locale-row">
            <span
              v-for="(locale, index) in i18n.global.availableLocales"
              :key="locale"
              :class="getLocaleClass(locale)"
              @click="switchLocale(locale)"
            >
              {{ locale }}<span v-if="index < i18n.global.availableLocales.length - 1"> | </span>
            </span>
          </div>
        </div>
        <AppBarMenu
          :is-light-theme="isScrolledOutsideHeader"
          :site-tree="appBarSiteTree"
        ></AppBarMenu>
      </template>
    </DpApp>

    <DpAppInsights
      :app="app"
      :instrumentation-key="instrumentationKey"
      :user-email="user.email"
      :object-id="userId"
      :router="$router"
    ></DpAppInsights>
  </div>
</template>

<style lang="scss" src="./App.scss"></style>